import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TourCard } from '../components/TourCard';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { FilteredResults } from '../components/filters/FilteredResults';
import { SCHEMAS } from '../lib/filterSchemas';
import { LocationSearch } from '../components/LocationSearch';
import { Seo } from '../components/Seo';
import { breadcrumb } from '../lib/structuredData';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { usePersistentState } from '../lib/usePersistentState';
import { Map, Compass, Info, Search } from 'lucide-react';

export default function Tours() {
  const { currency } = useApp();
  const [meta, setMeta] = useState(null);
  const [tours, setTours] = usePersistentState('tours:list', null);
  const [dest, setDest] = usePersistentState('tours:dest', null);
  const [q, setQ] = usePersistentState('tours:q', '');
  const [lastDest, setLastDest] = usePersistentState('tours:lastDest', '');

  const load = useCallback((destination = '') => {
    setLastDest(destination);
    setTours(null);
    api.toursList({ destination, currency }).then(({ data }) => setTours(data.tours)).catch(() => setTours([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  useEffect(() => { api.toursMeta().then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  // Restore the previous search on return; only auto-load a fresh pool when nothing is stored.
  useEffect(() => { if (tours === null) load(lastDest || ''); /* eslint-disable-next-line */ }, [load]);
  useEffect(() => { api.toursTrack('tour_impression', { surface: 'tours_home' }); }, []);

  const runSearch = () => {
    const destination = (dest?.slug || dest?.name || dest?.city || q || '').trim();
    api.toursTrack('tour_search', { q: destination });
    load(destination);
  };

  const filteredTours = tours;

  return (
    <Layout>
      <Seo title="Multi-Day Tours & Guided Trips"
        description="Compare guided, group, private and road-trip multi-day tours across trusted partners. Find the best-value tours to Japan, Bali, Thailand, Dubai, Goa and more on FlightDeals.in."
        path="/tours"
        jsonLd={breadcrumb([{ name: 'Home', path: '/' }, { name: 'Tours' }])} />
      <section className="relative bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium mb-3"><Compass className="h-3.5 w-3.5 text-accent" /> New · Tours vertical</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold flex items-center gap-2"><Map className="h-8 w-8 text-accent" /> Multi-day tours</h1>
          <p className="text-primary-foreground/80 mt-1 max-w-2xl">Guided, group, private & road-trip tours — compared across partners so you find the best value.</p>
          <div className="mt-5 rounded-2xl bg-card text-foreground p-4 grid gap-3 md:grid-cols-12 items-end" data-testid="tour-search">
            <div className="md:col-span-8">
              <LocationSearch vertical="tour" label="Where do you want to go?" placeholder="Destination, country or tour (e.g. Japan, Rajasthan, Bali)"
                value={dest} onChange={(p) => { setDest(p); setQ(p?.name || ''); if (p) load(p?.slug || p?.name || p?.city); }} testid="tour-destination-input" />
            </div>
            <div className="md:col-span-4"><Button onClick={runSearch} className="w-full h-12 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="tour-search-button"><Search className="h-4 w-4 mr-1.5" /> Search tours</Button></div>
          </div>
        </div>
      </section>

      {/* Destination-first discovery */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h2 className="font-display text-lg font-semibold mb-3">Explore by destination</h2>
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="tour-destinations">
          {(meta?.destinations || []).map((d) => (
            <Link key={d.slug} to={`/tours/${d.slug}`} className="group relative rounded-2xl overflow-hidden aspect-[4/3] card-elevate" data-testid={`tour-dest-${d.slug}`}>
              {d.image && <img src={d.image} alt={d.slug} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-2 left-2.5 text-white">
                <p className="font-semibold capitalize text-sm">{d.city || d.slug}</p>
                <p className="text-[11px] opacity-90">{d.tour_count} tour{d.tour_count === 1 ? '' : 's'} · {d.country}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Filters + grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div className="mb-4"><AffiliateDisclosure variant="banner" /></div>
        {!filteredTours ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-80 rounded-2xl" />)}</div>
        ) : (
          <FilteredResults schema={SCHEMAS.tour} items={filteredTours} testId="tours" countNoun="tour"
            gridClassName="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            onInteract={() => api.toursTrack('filter_used', {})}
            renderItem={(t) => <TourCard key={t.tour_id} t={t} currency={currency} />} />
        )}
        <p className="mt-6 text-xs text-muted-foreground flex items-start gap-1.5"><Info className="h-3.5 w-3.5 mt-0.5" /> Prices are indicative per person and set by partners. Sponsored tours are clearly labelled and never change a tour's rating, price or organic ranking.</p>
      </section>
    </Layout>
  );
}
